import { makeStyles, shorthands } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";

const fadeSlideIn = {
  from: {
    opacity: 0,
    transform: "translateY(8px)",
  },
  to: {
    opacity: 1,
    transform: "translateY(0)",
  },
};

export const useCopyDialogStyles = makeStyles({
  dialogSurface: {
    borderRadius: designTokens.borderRadius.lg,
    ...shorthands.padding(
      designTokens.spacing[6],
      designTokens.spacing[6],
      designTokens.spacing[6],
      designTokens.spacing[6],
    ),
    width: "648px",
    maxWidth: "90vw",
    animationName: fadeSlideIn,
    animationDuration: designTokens.transition.base,
    animationFillMode: "both",
    "@media (max-width: 640px)": {
      minWidth: "unset",
      width: `calc(100vw - ${designTokens.spacing[8]})`,
      maxWidth: "100%",
      ...shorthands.padding(
        designTokens.spacing[4],
        designTokens.spacing[3],
        designTokens.spacing[4],
        designTokens.spacing[3],
      ),
    },
    "@media (prefers-reduced-motion: reduce)": {
      animationDuration: "0.01ms",
    },
  },

  dialogBody: {
    display: "block",
    boxSizing: "border-box",
    ...shorthands.padding(designTokens.spacing[2], designTokens.spacing[1]),
    ...shorthands.margin(0, `-${designTokens.spacing[1]}`),
    overflowY: "visible",
    "@media (max-width: 640px)": {
      ...shorthands.padding(designTokens.spacing[2], designTokens.spacing[1]),
      minHeight: "auto",
    },
  },

  dialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: designTokens.spacing[1],
  },

  dialogTitle: {
    ...shorthands.padding(0),
    margin: 0,
    flex: 1,
  },

  rootColumn: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "inherit",
  },

  subtitle: {
    display: "block",
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[700],
    marginBottom: designTokens.spacing[1],
  },

  warningBannerWrapper: {
    marginBottom: designTokens.spacing[2],
  },

  shareWithLabel: {
    display: "block",
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[700],
    marginBottom: designTokens.spacing[2],
  },

  copyOptionsRow: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },

  radioGroup: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
    width: "100%",
  },

  radioCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: designTokens.spacing[3],
    width: "100%",
    boxSizing: "border-box",
    ...shorthands.padding(designTokens.spacing[3]),
    ...shorthands.border("1px", "solid", designTokens.neutral[200]),
    borderRadius: designTokens.borderRadius.lg,
    cursor: "pointer",
    transitionProperty: "border-color, background-color",
    transitionDuration: designTokens.transition.fast,
    backgroundColor: designTokens.neutral.white,
    ":hover": {
      ...shorthands.borderColor(designTokens.neutral[300]),
      backgroundColor: designTokens.neutral[50],
    },
    ":focus-within": {
      boxShadow: `0 0 0 2px ${designTokens.neutral.white}, 0 0 0 4px ${designTokens.accent[500]}`,
    },
  },

  radioCardSelected: {
    ...shorthands.borderColor(designTokens.accent[500]),
    backgroundColor: designTokens.accent[50],
    ":hover": {
      ...shorthands.borderColor(designTokens.accent[600]),
      backgroundColor: designTokens.accent[50],
    },
  },

  radioCardContent: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
  },

  radioCardTitle: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[900],
  },

  radioCardDescription: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
  },

  sectionSpacing: {
    position: "relative",
    zIndex: 2,
    animationName: fadeSlideIn,
    animationDuration: designTokens.transition.base,
    animationFillMode: "both",
    "@media (prefers-reduced-motion: reduce)": {
      animationDuration: "0.01ms",
    },
  },

  toggleWrapper: {
    position: "relative",
    zIndex: 1,
    marginTop: designTokens.spacing[3],
    paddingTop: designTokens.spacing[3],
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderTopColor: designTokens.neutral[200],
  },

  emailSectionDivider: {
    marginTop: designTokens.spacing[3],
    ...shorthands.padding(designTokens.spacing[4]),
    ...shorthands.border("1px", "solid", designTokens.neutral[200]),
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: designTokens.neutral[50],
    animationName: fadeSlideIn,
    animationDuration: designTokens.transition.base,
    animationFillMode: "both",
    "@media (prefers-reduced-motion: reduce)": {
      animationDuration: "0.01ms",
    },
  },

  emailFields: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
  },

  emailFieldLabel: {
    display: "block",
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[500],
    textTransform: "none" as const,
    marginBottom: designTokens.spacing[1],
  },

  workspacePickerWrapper: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    animationName: fadeSlideIn,
    animationDuration: designTokens.transition.base,
    animationFillMode: "both",
    "@media (prefers-reduced-motion: reduce)": {
      animationDuration: "0.01ms",
    },
  },

  confirmDialogActions: {
    justifyContent: "flex-end",
  },

  inlineHintBanner: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    ...shorthands.padding(designTokens.spacing[3]),
    backgroundColor: designTokens.accent[50],
    ...shorthands.border("1px", "solid", designTokens.accent[200]),
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.xs,
    color: designTokens.accent[700],
    marginBottom: designTokens.spacing[4],
  },

  inlineHintIcon: {
    width: designTokens.iconSize.sm,
    height: designTokens.iconSize.sm,
    flexShrink: 0,
    color: designTokens.accent[600],
  },

  formField: {
    marginBottom: designTokens.spacing[4],
  },

  formFieldLast: {
    marginBottom: 0,
  },

  spinnerWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    minHeight: "200px",
  },
});
